import React, {
    useContext, useEffect, useMemo, useState 
} from 'react';
import {
    Box, Slide, Typography, useTheme 
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CardContainer from '../../components/cardContainer';
import Card from '../../components/card';
import ProgressBar from '../../components/progressBar';
import {
    DiagnosticFormContext 
} from '../../../../contexts/form';
import {
    TypographyVariants, TypographyWeights 
} from '../../../../figmaTheme';

type Props = {
    position: number
};

export const CostBaseline = ({
    position
}: Props): JSX.Element => {
    const formContext = useContext(DiagnosticFormContext);
    const drinksBaseline = useMemo(() => {
        return formContext.answers?.totalDrinksBaseline;
    }, [formContext.answers?.totalDrinksBaseline]);
    const drinkCost = useMemo(() => (
        formContext.answers?.drinkCost
    ), [formContext.answers?.drinkCost]);
    const [skipAnimation, setSkipAnimation] = useState(false);
    const useStyles = useMemo(() => makeStyles((theme) => ({
        scrollableContainer: {
            overflow: 'scroll',
            scrollbarWidth: 'none',
            minHeight: '100%',
            width: '100vw',
            maxWidth: '692px',
            transform: 'translate(-24px, 0)',
            padding: '0 24px'
        },
        content: {
            display: 'flex',
            flexDirection: 'column',
            rowGap: '70px'
        },
        costCircle: {
            width: '250px',
            height: '250px',
            borderRadius: '50%',
            backgroundColor: theme.palette.secondary.light,
            outline: `16px solid ${theme.palette.secondary['200']}`,
            display: 'flex',
            flexDirection: 'column',
            placeSelf: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'scale(0)',
            animation: `$grow ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1}s`,
        },
        icon: {
            marginBottom: '16px',
        },
        cost: {
            fontWeight: TypographyWeights.BOLD,
            marginBottom: '8px',
            opacity: 0,
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        text: {
            opacity: 0,
            textAlign: 'center',
            animation: `$fadeIn ${skipAnimation ? 0 : 0.5}s forwards ${skipAnimation ? 0 : 1.5}s`,
        },
        '@keyframes grow': {
            '0%': {
                transform: 'scale(0)' 
            },
            '100%': {
                transform: 'scale(1)' 
            },
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0' 
            },
            '100%': {
                opacity: '1' 
            },
        }
    })), [skipAnimation]);
    const classes = useStyles();
    const theme = useTheme();

    const onClick = () => {
        if (!skipAnimation) setSkipAnimation(true);
        else formContext.goNext();
    };

    useEffect(() => {
        setTimeout(() => {
            setSkipAnimation(true);
        }, 2000);
    });

    return (
        <CardContainer
            sectionKey={'habits'}
            goBack={formContext.goBack}
            goNext={onClick}
            hasGradient
        >
            <Card>
                <ProgressBar
                    currentPosition={position}
                    sectionKey={'habits'}
                />
                <Box className={classes.scrollableContainer}>
                    <Slide
                        in={true}
                        direction={formContext.screenDirection}
                        timeout={500}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Box className={classes.content}>
                            <Typography
                                variant={TypographyVariants.H3}
                                dangerouslySetInnerHTML={{
                                    __html: `At ${drinksBaseline} drinks per week, today you're spending around <span style="font-weight:${TypographyWeights.BOLD};color:${theme.palette.secondary.main};">$${drinksBaseline * drinkCost} per week</span> on alcohol.` 
                                }}
                            />
                            <Box className={classes.costCircle}>
                                <svg
                                    className={classes.icon}
                                    width="34"
                                    height="40"
                                    viewBox="0 0 34 40"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M0.863502 0.67749C1.35124 0.416462 1.94306 0.445071 2.40334 0.751927L6.71415 3.6258L11.025 0.751927C11.5288 0.416028 12.1852 0.416028 12.6891 0.751927L16.9999 3.6258L21.3107 0.751927C21.8145 0.416028 22.4709 0.416028 22.9748 0.751927L27.2856 3.6258L31.5964 0.751927C32.0567 0.445071 32.6485 0.416462 33.1362 0.67749C33.624 0.938519 33.9284 1.44681 33.9284 2V38C33.9284 38.8284 33.2569 39.5 32.4284 39.5H1.57129C0.742862 39.5 0.0712891 38.8284 0.0712891 38V2C0.0712891 1.44681 0.375766 0.938519 0.863502 0.67749ZM3.07129 4.80278V36.5H30.9284V4.80278L28.1176 6.67665C27.6138 7.01255 26.9574 7.01255 26.4535 6.67665L22.1427 3.80278L17.8319 6.67665C17.3281 7.01255 16.6717 7.01255 16.1678 6.67665L11.857 3.80278L7.5462 6.67665C7.04235 7.01255 6.38595 7.01255 5.8821 6.67665L3.07129 4.80278Z"
                                        fill={theme.palette.secondary.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M16.3117 16.148C15.0779 16.148 14.1257 17.1002 14.1257 18.3341C14.1257 18.7503 14.2128 19.0134 14.3152 19.1926C14.4194 19.3749 14.5814 19.5435 14.8368 19.7067C15.4037 20.0689 16.215 20.2852 17.3044 20.5576C17.3364 20.5655 17.3686 20.5736 17.4011 20.5817C18.3615 20.8214 19.5654 21.1218 20.5148 21.7284C21.0338 22.06 21.517 22.5043 21.8645 23.1124C22.2137 23.7237 22.3847 24.4284 22.3847 25.2166C22.3847 27.8369 20.3086 29.9131 17.6882 29.9131H16.3117C14.0167 29.9131 12.1307 28.3193 11.7047 26.1463C11.5714 25.466 12.0148 24.8064 12.6951 24.673C13.3754 24.5397 14.035 24.9831 14.1684 25.6634C14.3646 26.6646 15.2233 27.4026 16.3117 27.4026H17.6882C18.9221 27.4026 19.8743 26.4504 19.8743 25.2166C19.8743 24.8003 19.7872 24.5372 19.6848 24.358C19.5806 24.1757 19.4185 24.0071 19.1632 23.844C18.5963 23.4818 17.785 23.2654 16.6955 22.9931C16.6636 22.9851 16.6314 22.9771 16.5989 22.969C15.6385 22.7293 14.4346 22.4289 13.4851 21.8222C12.9662 21.4907 12.483 21.0463 12.1355 20.4382C11.7862 19.827 11.6152 19.1222 11.6152 18.3341C11.6152 15.7137 13.6914 13.6376 16.3117 13.6376H17.6882C19.9817 13.6376 21.8673 15.2296 22.295 17.402C22.429 18.0822 21.9861 18.7421 21.3059 18.876C20.6257 19.01 19.9657 18.5671 19.8318 17.8869C19.6346 16.885 18.7755 16.148 17.6882 16.148H16.3117Z"
                                        fill={theme.palette.secondary.main}
                                    />
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M17.0001 11.8932C17.4623 11.8932 17.8369 12.2679 17.8369 12.73L17.8367 30.8207C17.8367 31.2828 17.462 31.6575 16.9999 31.6575C16.5377 31.6575 16.163 31.2828 16.163 30.8206L16.1632 12.73C16.1633 12.2678 16.5379 11.8932 17.0001 11.8932Z"
                                        fill={theme.palette.secondary.main}
                                    />
                                </svg>
                                <Typography
                                    variant={TypographyVariants.XXL}
                                    className={classes.cost}
                                >
                                    {`$${drinksBaseline * drinkCost}`}
                                </Typography>
                                <Typography
                                    variant={TypographyVariants.H6}
                                    className={classes.text}
                                >
                                    Weekly Alcohol Spend
                                </Typography>
                            </Box>
                            <Box
                                style={{
                                    height: '300px' 
                                }}
                            />
                        </Box>
                    </Slide>
                </Box>
            </Card>
        </CardContainer>
    );
};

export default CostBaseline;
